import React, { useState } from "react";
import { Link, NavLink, useNavigate, Outlet, useLocation } from "react-router-dom";
import {
  LayoutDashboard,
  Users,
  FileText,
  Receipt,
  Inbox,
  BookOpen,
  Settings,
  LogOut,
  Mic,
  Menu,
  X,
  CreditCard,
  Crown,
  Shield,
  Mail,
  Package,
  Users2,
} from "lucide-react";
import { useAuth } from "../lib/auth";
import { useI18n } from "../lib/i18n";
import { api } from "../lib/api";
import LangSwitcher from "./LangSwitcher";
import CompanySwitcher from "./CompanySwitcher";
import NotificationBell from "./NotificationBell";
import VoiceAgentModal from "./VoiceAgentModal";
import BillingBanner from "./BillingBanner";
import { Toaster } from "./ui/sonner";

export default function AppShell() {
  const { user, activeCompany, logout, loading } = useAuth();
  const { t } = useI18n();
  const navigate = useNavigate();
  const [voiceOpen, setVoiceOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [isSuperadmin, setIsSuperadmin] = useState(false);
  const loc = useLocation();

  React.useEffect(() => {
    if (!loading && !user) navigate("/login");
  }, [loading, user, navigate]);

  React.useEffect(() => {
    setMobileOpen(false);
  }, [loc.pathname]);

  // Detect super-admin (HOSYS owner) for showing the admin link
  React.useEffect(() => {
    if (!user) return;
    let alive = true;
    (async () => {
      try {
        const r = await api.get("/admin/me");
        if (alive) setIsSuperadmin(!!r.data.is_superadmin);
      } catch {
        if (alive) setIsSuperadmin(false);
      }
    })();
    return () => { alive = false; };
  }, [user]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-slate-400">{t("common.loading")}</div>
      </div>
    );
  }
  if (!user) return null;

  const navItems = [
    { to: "/app", icon: LayoutDashboard, label: t("nav.dashboard"), end: true },
    { to: "/app/clients", icon: Users, label: t("nav.clients") },
    { to: "/app/quotes", icon: FileText, label: t("nav.quotes") },
    { to: "/app/invoices", icon: Receipt, label: t("nav.invoices") },
    { to: "/app/received-invoices", icon: Inbox, label: t("nav.received") },
    { to: "/app/mailbox", icon: Mail, label: t("nav.mailbox") },
    { to: "/app/products", icon: Package, label: t("nav.products") },
    { to: "/app/suppliers", icon: Users2, label: t("nav.suppliers") },
    { to: "/app/tax-books", icon: BookOpen, label: t("nav.tax_books") },
    { to: "/app/pricing", icon: CreditCard, label: t("nav.pricing") },
    { to: "/app/security", icon: Shield, label: t("nav.security") },
    { to: "/app/settings", icon: Settings, label: t("nav.settings") },
    ...(isSuperadmin
      ? [{ to: "/app/admin", icon: Crown, label: "Admin", accent: "purple" }]
      : []),
  ];

  return (
    <div className="min-h-screen flex">
      {/* Sidebar */}
      <aside
        className={`fixed inset-y-0 left-0 z-40 w-64 transform transition-transform glass border-r border-white/10 ${
          mobileOpen ? "translate-x-0" : "-translate-x-full"
        } lg:translate-x-0 lg:static`}
        data-testid="app-sidebar"
      >
        <div className="h-16 flex items-center justify-between px-5 border-b border-white/5">
          <Link to="/app" className="flex items-center gap-2 group">
            {/* Actual HO-SYS brand logo (silver H with blue circuit).
                Background image so the logo's own black backdrop merges
                seamlessly with the dark sidebar chrome. */}
            <img
              src="/logo-hosys.png"
              alt="HO-SYS"
              className="w-9 h-9 rounded-lg object-cover shadow-[0_0_20px_rgba(59,130,246,0.4)]"
            />
            <span className="font-display font-bold tracking-tight text-white">
              HO-SYS
            </span>
          </Link>
          <button
            type="button"
            className="lg:hidden text-slate-400"
            onClick={() => setMobileOpen(false)}
          >
            <X size={20} />
          </button>
        </div>

        <nav className="px-3 py-5 space-y-1">
          {navItems.map((it) => (
            <NavLink
              key={it.to}
              to={it.to}
              end={it.end}
              data-testid={`nav-${it.to.split("/").pop() || "dashboard"}`}
              className={({ isActive }) => {
                const isAdmin = it.accent === "purple";
                if (isActive) {
                  return isAdmin
                    ? "flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm bg-purple-500/15 text-purple-200 border border-purple-500/30 shadow-[inset_0_0_20px_rgba(168,85,247,0.18)]"
                    : "flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm bg-blue-500/15 text-blue-200 border border-blue-500/30 shadow-[inset_0_0_20px_rgba(59,130,246,0.1)]";
                }
                return isAdmin
                  ? "flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-purple-300 hover:text-purple-100 hover:bg-purple-500/10"
                  : "flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-slate-400 hover:text-white hover:bg-white/5";
              }}
            >
              <it.icon size={17} />
              <span>{it.label}</span>
            </NavLink>
          ))}
        </nav>

        <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-white/5">
          <button
            type="button"
            data-testid="voice-fab"
            onClick={() => setVoiceOpen(true)}
            className="w-full flex items-center justify-center gap-2 bg-gradient-to-r from-blue-600 to-blue-500 hover:from-blue-500 hover:to-blue-400 text-white py-2.5 rounded-lg font-medium shadow-[0_0_24px_rgba(59,130,246,0.35)] transition-all"
          >
            <Mic size={16} />
            <span>{t("dashboard.voice_cta")}</span>
          </button>
          <button
            type="button"
            data-testid="logout-btn"
            onClick={logout}
            className="mt-3 w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-slate-400 hover:text-white hover:bg-white/5 transition-all"
          >
            <LogOut size={15} />
            <span>{t("nav.logout")}</span>
          </button>
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 min-w-0">
        <header className="sticky top-0 z-30 glass border-b border-white/5">
          <div className="h-16 px-5 lg:px-8 flex items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <button
                type="button"
                className="lg:hidden text-slate-300"
                onClick={() => setMobileOpen(true)}
              >
                <Menu size={22} />
              </button>
              <CompanySwitcher />
            </div>
            <div className="flex items-center gap-3">
              <NotificationBell />
              <LangSwitcher />
              <div className="hidden sm:flex items-center gap-2 px-3 py-1.5 rounded-full border border-white/10 bg-white/5">
                <div className="w-7 h-7 rounded-full bg-gradient-to-br from-blue-500 to-violet-500 flex items-center justify-center text-xs font-bold">
                  {user.full_name?.[0] || "U"}
                </div>
                <span className="text-xs text-slate-300 hidden md:inline">
                  {user.full_name}
                </span>
              </div>
            </div>
          </div>
        </header>

        <main className="p-4 sm:p-6 lg:p-8 max-w-[1600px] mx-auto overflow-x-hidden">
          <BillingBanner />
          <Outlet context={{ openVoice: () => setVoiceOpen(true) }} />
        </main>
      </div>

      <VoiceAgentModal open={voiceOpen} onOpenChange={setVoiceOpen} />
      <Toaster position="top-right" theme="dark" richColors />
    </div>
  );
}
